import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import {
    Search,
    Upload,
    Plus,
    Download,
    X,
    Calendar,
    Edit2,
    Trash2,
    CloudUpload,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    Loader2
} from 'lucide-react';
import './HolidaysStyle.css';

// FORCE LOCAL URL for immediate sync capability
const HOLIDAY_API_URL = `${API_URL}/holidays`;

const getToken = () => localStorage.getItem('zTaskAuthToken');

const getUserRole = () => {
    try {
        const userDetailsStr = localStorage.getItem('zTaskUserDetails');
        if (!userDetailsStr) return 'EMPLOYEE';
        const userDetails = JSON.parse(userDetailsStr);
        return userDetails?.role?.name || 'EMPLOYEE';
    } catch (error) {
        return 'EMPLOYEE';
    }
};

const Holidays = () => {
    // --- State ---
    const [holidays, setHolidays] = useState([]);
    const [holidayTypes, setHolidayTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('EMPLOYEE');

    // UI State
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [newHoliday, setNewHoliday] = useState({ name: '', date: '', holiday_type_id: '' });
    const [editingHoliday, setEditingHoliday] = useState(null);

    // --- Initialization ---
    useEffect(() => {
        console.log('Current Holiday API URL:', HOLIDAY_API_URL); // Debugging
        setUserRole(getUserRole());
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([fetchHolidays(), fetchHolidayTypes()]);
            setError(null);
        } catch (err) {
            console.error("Init Error:", err);
            setError("Failed to load holiday data.");
        } finally {
            setLoading(false);
        }
    };

    const canEdit = ['HR', 'ADMIN', 'SUPER_ADMIN'].includes(userRole?.toUpperCase());
    const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

    // --- Helpers ---
    const formatDateForApi = (dateStr) => {
        if (!dateStr) return '';
        // If YYYY-MM-DD -> DD-MM-YYYY
        if (dateStr.includes('-')) {
            const parts = dateStr.split('-');
            if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
    };

    const parseApiDate = (dateStr) => {
        if (!dateStr) return new Date();
        const parts = dateStr.split('-');
        // Handles "dd-mm-yyyy"
        if (parts.length === 3 && parts[2].length === 4) {
            return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
        }
        return new Date(dateStr);
    };

    const formatDisplayDate = (dateStr) => {
        const date = parseApiDate(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const getDayName = (dateStr) => {
        const date = parseApiDate(dateStr);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-US', { weekday: 'long' });
    };

    // --- API Calls ---
    const fetchHolidays = async () => {
        const response = await axios.get(HOLIDAY_API_URL, getAuthHeader());
        setHolidays(response.data);
    };

    const fetchHolidayTypes = async () => {
        try {
            const url = `${API_URL}/get-column/common_master/holiday_type`;
            const response = await axios.get(url, getAuthHeader());
            setHolidayTypes(response.data);
        } catch (err) {
            console.error("Holiday Types Fetch Error:", err);
            setHolidayTypes([]);
        }
    };

    const handleSync = async () => {
        if (!window.confirm(`Sync public holidays for ${selectedYear}? Missing ones will be added automatically.`)) return;
        setSyncing(true);
        try {
            const response = await axios.post(`${HOLIDAY_API_URL}/sync`, {
                year: parseInt(selectedYear),
                countryCode: 'IN'
            }, getAuthHeader());

            alert(`Sync Complete! Added: ${response.data.summary.added}, Skipped: ${response.data.summary.skipped}`);
            await fetchHolidays();
        } catch (err) {
            alert('Sync failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setSyncing(false);
        }
    };

    const handleDelete = async (id) => {
        if (!canEdit) return;
        if (!window.confirm('Remove this holiday from the list?')) return;
        try {
            await axios.delete(`${HOLIDAY_API_URL}/${id}`, getAuthHeader());
            setHolidays(prev => prev.filter(h => h.id !== id));
        } catch (err) {
            alert('Error deleting: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleSaveHoliday = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: newHoliday.name,
                date: formatDateForApi(newHoliday.date),
                holiday_type_id: newHoliday.holiday_type_id
            };

            if (editingHoliday) {
                await axios.put(`${HOLIDAY_API_URL}/${editingHoliday.id}`, payload, getAuthHeader());
            } else {
                await axios.post(HOLIDAY_API_URL, payload, getAuthHeader());
            }

            setShowAddModal(false);
            setEditingHoliday(null);
            setNewHoliday({ name: '', date: '', holiday_type_id: '' });
            fetchHolidays();
        } catch (err) {
            alert('Error saving: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleEdit = (holiday) => {
        const date = parseApiDate(holiday.date);
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');

        setNewHoliday({
            name: holiday.name,
            date: `${y}-${m}-${d}`,
            holiday_type_id: holiday.holiday_type_id || ''
        });
        setEditingHoliday(holiday);
        setShowAddModal(true);
    };

    const handleImport = async () => {
        if (!importFile) return;
        const formData = new FormData();
        formData.append('file', importFile);
        try {
            await axios.post(`${HOLIDAY_API_URL}/import`, formData, {
                headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
            });
            setShowImportModal(false);
            setImportFile(null);
            fetchHolidays();
        } catch (err) {
            alert('Import failed: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${HOLIDAY_API_URL}/template`, {
                ...getAuthHeader(),
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'holiday_import_template.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            alert('Failed to download template: ' + (err.response?.data?.message || err.message));
        }
    };

    // --- Derived Data ---
    const filteredHolidays = holidays.filter(h => {
        const matchesSearch = h.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesYear = parseApiDate(h.date).getFullYear().toString() === selectedYear;
        return matchesSearch && matchesYear;
    });

    const upNext = [...holidays]
        .filter(h => parseApiDate(h.date) >= new Date().setHours(0, 0, 0, 0))
        .sort((a, b) => parseApiDate(a.date) - parseApiDate(b.date))[0];

    const totalPages = Math.ceil(filteredHolidays.length / itemsPerPage);
    const paginatedItems = filteredHolidays.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="holiday-page-container">
            {/* Header */}
            <header className="page-top-bar">
                <div className="header-content">
                    <h1>Holiday Management</h1>
                    <p className="header-subtitle">Manage official holidays and seasonal breaks</p>
                </div>
                {canEdit && (
                    <div className="header-actions">
                        <button className="btn-secondary" onClick={() => setShowImportModal(true)}>
                            <Upload size={16} /> Bulk Import
                        </button>
                        <button className="btn-secondary" onClick={handleSync} disabled={syncing}>
                            {syncing ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                            Sync from API
                        </button>
                        <button className="btn-primary-add" onClick={() => { setEditingHoliday(null); setShowAddModal(true); }}>
                            <Plus size={16} /> New Holiday
                        </button>
                    </div>
                )}
            </header>

            {/* Dash Cards */}
            <div className="dashboard-grid">
                <div className="hero-card">
                    <div className="hero-label"><Calendar size={12} /> Upcoming Holiday</div>
                    {upNext ? (
                        <div className="hero-content">
                            <h2>{upNext.name}</h2>
                            <div className="hero-date">{formatDisplayDate(upNext.date)} • {getDayName(upNext.date)}</div>
                        </div>
                    ) : (
                        <div className="hero-content"><h2>No upcoming holidays</h2></div>
                    )}
                </div>
                <div className="stats-card">
                    <div className="stats-title">Total Holidays</div>
                    <div className="stats-number">{filteredHolidays.length}</div>
                    <div className="stats-desc">In Calendar Year {selectedYear}</div>
                </div>
            </div>

            {/* Controls */}
            <div className="controls-bar">
                <div className="search-wrapper">
                    <Search className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by holiday name..."
                        className="search-input"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <select className="year-dropdown" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
                    {Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                        <option key={year} value={year}>CY {year}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className="table-container">
                <div className="table-header-row">
                    <div className="header-cell">Holiday Name</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">Day</div>
                    <div className="header-cell">Category</div>
                    <div className="header-cell">Status</div>
                    <div className="header-cell" style={{ textAlign: 'right' }}>Actions</div>
                </div>

                <div className="table-scroll-body">
                    {loading ? (
                        <div className="app-loader">Loading calendar...</div>
                    ) : paginatedItems.length === 0 ? (
                        <div className="empty-state">No holidays found for this criteria.</div>
                    ) : (
                        paginatedItems.map(h => {
                            const hDate = parseApiDate(h.date);
                            const today = new Date().setHours(0, 0, 0, 0);
                            const status = hDate.getTime() < today ? 'past' : (hDate.getTime() === today ? 'today' : 'upcoming');

                            return (
                                <div key={h.id} className="holiday-row">
                                    <div className="name-cell">{h.name}</div>
                                    <div className="date-cell">{formatDisplayDate(h.date)}</div>
                                    <div className="day-cell">{getDayName(h.date)}</div>
                                    <div className="type-cell">
                                        <span className="type-badge">{h.type_name || h.type_value || 'Default'}</span>
                                    </div>
                                    <div className="status-cell">
                                        <span className={`status-badge ${status}`}>{status}</span>
                                    </div>
                                    <div className="actions-cell">
                                        {canEdit && (
                                            <>
                                                {status !== 'past' && (
                                                    <button className="action-btn" onClick={() => handleEdit(h)}><Edit2 size={14} /></button>
                                                )}
                                                {status !== 'past' && (
                                                    <button className="action-btn trash" onClick={() => handleDelete(h.id)}><Trash2 size={14} /></button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="table-footer">
                    <div className="footer-text">
                        Showing {filteredHolidays.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredHolidays.length)} of {filteredHolidays.length}
                    </div>
                    <div className="pagination-wrapper">
                        <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                            <ChevronLeft size={16} />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <button
                                key={i + 1}
                                className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                onClick={() => setCurrentPage(i + 1)}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button className="page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal: Add/Edit */}
            {showAddModal && (
                <div className="form-overlay">
                    <form className="form-card" onSubmit={handleSaveHoliday}>
                        <div className="form-header-modal">
                            <h3>{editingHoliday ? 'Edit Holiday' : 'Create New Holiday'}</h3>
                            <button type="button" className="action-btn" onClick={() => setShowAddModal(false)}><X size={18} /></button>
                        </div>
                        <div className="form-body-modal">
                            <div className="form-group">
                                <label className="form-label">Name</label>
                                <input className="form-input" value={newHoliday.name} required onChange={e => setNewHoliday({ ...newHoliday, name: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Date</label>
                                <input type="date" className="form-input" value={newHoliday.date} required onChange={e => setNewHoliday({ ...newHoliday, date: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Category</label>
                                <select className="form-input" value={newHoliday.holiday_type_id} required onChange={e => setNewHoliday({ ...newHoliday, holiday_type_id: e.target.value })}>
                                    <option value="">Select Category</option>
                                    {holidayTypes.map(t => <option key={t.id} value={t.id}>{t.holiday_type || t.value}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="form-footer-modal">
                            <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                            <button type="submit" className="btn-primary-add">Save Holiday</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Modal: Import */}
            {showImportModal && (
                <div className="form-overlay">
                    <div className="form-card import-card">
                        <div className="form-header-modal">
                            <h3>Bulk Import Holidays</h3>
                            <button className="action-btn" onClick={() => setShowImportModal(false)}><X size={18} /></button>
                        </div>
                        <div className="form-body-modal">
                            <div className="drop-zone" onClick={() => document.getElementById('fileInp').click()}>
                                <CloudUpload size={40} />
                                <p>Click to upload CSV template</p>
                                <input id="fileInp" type="file" hidden accept=".csv" onChange={e => setImportFile(e.target.files[0])} />
                            </div>
                            {importFile && <div className="file-info-box"><span>{importFile.name}</span> <Trash2 size={14} onClick={() => setImportFile(null)} /></div>}
                        </div>
                        <div className="form-footer-modal">
                            <button className="btn-secondary" onClick={handleDownloadTemplate} type="button">
                                <Download size={16} /> Download Template
                            </button>
                            <div style={{ flex: 1 }}></div>
                            <button className="btn-secondary" onClick={() => setShowImportModal(false)} type="button">Cancel</button>
                            <button className="btn-primary-add" disabled={!importFile} onClick={handleImport} type="button">Import Now</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Holidays;